/**
 * @name calculateCPU
 * @description 解析 CPU 值 calculateCPU
 * @example
 * // 测试用例
 * console.log(calculateCPU(['100m', '50m'])) // '150m'
 * console.log(calculateCPU(['1', '500m'])) // 1.5
 * console.log(calculateCPU([1, '500m'])) // 1.5
 * console.log(calculateCPU(['1.5', '500m'])) // 2
 * console.log(calculateCPU(['0.1', '100m'])) // 0.2
 * console.log(calculateCPU(['1000m', '200m'], { autoConvertToNumber: false })) // '1200m'
 *
 * // 强制单位测试
 * console.log(calculateCPU(['1.5'], { forceUnit: 'm' })) // '1500m'
 * console.log(calculateCPU(['1500m'], { forceUnit: 'number' })) // 1.5
 *
 */
export function calculateCPU(
  cpuValues: CPUValue[],
  options: CalculateCPUOptions = { autoConvertToNumber: true },
): string | number {
  let total = 0;
  let hasDecimal = false;

  // 1. 统一转换为核数（number）计算
  for (const value of cpuValues) {
    const parsed = parseCPUValue(value);
    total += parsed;
    if (!hasDecimal && !Number.isInteger(parsed)) {
      hasDecimal = true;
    }
  }

  // 2. 处理强制单位
  if (options.forceUnit === 'm') {
    return `${Math.round(total * 1000)}m`;
  }
  if (options.forceUnit === 'number') {
    return hasDecimal || total % 1 !== 0 ? total : Math.round(total);
  }

  // 3. 自动单位选择
  const totalM = Math.round(total * 1000);

  // 3.1 如果配置了自动转换且达到1000m
  if (options.autoConvertToNumber !== false && totalM >= 1000) {
    const cores = totalM / 1000;
    return hasDecimal || cores % 1 !== 0 ? cores : Math.round(cores);
  }

  // 3.2 否则返回m单位
  return `${totalM}m`;
}

/**
 * @name calculateMemory
 * @description 解析内存值 calculateMemory
 * @example
 * // 测试用例
 * console.log(calculateMemory(['100Mi', '200Mi'])); // '300Mi'
 * console.log(calculateMemory([undefined, '512Mi']));   // '512Mi'
 *
 * // 配置最小单位
 * console.log(calculateMemory(['1Gi', '512Mi'], { minUnit: 'Mi' })); // '1536Mi'
 * console.log(calculateMemory(['1Gi', '512Mi'], { minUnit: 'Gi' })); // '1.5Gi'
 * console.log(calculateMemory(['1024Ki'], { minUnit: 'Mi' }));       // '1Mi'
 *
 * // 强制指定单位
 * console.log(calculateMemory([undefined, '512Mi'], { forceUnit: 'Mi' }));   // '512Mi'
 * console.log(calculateMemory(['1Gi'], { forceUnit: 'Mi' }));        // '1024Mi'
 * console.log(calculateMemory(['1500Ki'], { forceUnit: 'Mi' }));     // '1.46Mi'
 *
 * // 自定义小数位数
 * console.log(calculateMemory(['1500Ki'], { forceUnit: 'Mi', decimalPlaces: 3 }));  // '1.465Mi'
 * console.log(calculateMemory(['1500Ki'], { forceUnit: 'Mi', decimalPlaces: 0 }));  // '1Mi'
 */
export function calculateMemory(
  memoryValues: MemoryValue[],
  options: CalculateMemoryOptions = {},
): string {
  if (memoryValues.length === 0) return '0B';

  let totalBytes = 0;
  for (const value of memoryValues) {
    const { bytes } = parseMemory(value);
    totalBytes += bytes;
  }

  // 确定输出单位
  const outputUnit = findAppropriateUnit(totalBytes, options);
  const bytesInUnit = memoryUnits[outputUnit];
  const convertedValue = totalBytes / bytesInUnit;

  // 格式化输出（整数不显示小数，非整数保留指定位数）
  const decimalPlaces = options.decimalPlaces ?? 2;
  const formattedValue =
    convertedValue % 1 === 0
      ? convertedValue.toString()
      : convertedValue.toFixed(decimalPlaces).replace(/\.?0+$/, '');

  return `${formattedValue}${outputUnit}`;
}

/** ============================ CPU 核心合计类型和工具函数 ==================================== */
type CPUValue = string | number;

interface CalculateCPUOptions {
  autoConvertToNumber?: boolean; // 是否自动将1000m转为1（默认true）
  forceUnit?: 'm' | 'number'; // 强制输出单位（优先级最高）
}

function parseCPUValue(value: CPUValue): number {
  if (typeof value === 'number') return value;
  if (typeof value === 'string') {
    // 处理字符串数字 '1' 或 '1.5'
    if (/^\d*\.?\d+$/.test(value)) {
      return parseFloat(value);
    }

    // 处理带单位的 '500m'
    if (value.endsWith('m')) {
      const num = value.slice(0, -1);
      if (/^\d+$/.test(num)) {
        return parseInt(num, 10) / 1000;
      }
    }
  }
  return 0;
}

/** ============================ 内存合计类型和工具函数 ==================================== */
type MemoryValue = string | number | undefined;
type MemoryUnit = 'B' | 'Ki' | 'Mi' | 'Gi' | 'Ti' | 'k' | 'M' | 'G' | 'T' | 'm';

interface ParsedMemory {
  bytes: number;
  unit: string;
}

interface CalculateMemoryOptions {
  /** 可配置的最小单位，默认是当前数组传入的最小单位 */
  minUnit?: MemoryUnit;
  /** 强制指定输出单位（优先级高于 minUnit） */
  forceUnit?: MemoryUnit;
  /** 保留小数位数，默认为 2 */
  decimalPlaces?: number;
}

const memoryUnits: Record<MemoryUnit, number> = {
  B: 1,
  Ki: 1024,
  Mi: 1024 ** 2,
  Gi: 1024 ** 3,
  Ti: 1024 ** 4,
  k: 1000,
  M: 1000 ** 2,
  G: 1000 ** 3,
  T: 1000 ** 4,
  m: 1 / 1000,
};

const unitPriority: MemoryUnit[] = ['B', 'k', 'M', 'G', 'T', 'Ki', 'Mi', 'Gi', 'Ti'];

function parseMemory(value: MemoryValue): ParsedMemory {
  if (typeof value === 'number') {
    return { bytes: value, unit: 'B' };
  }

  if (typeof value === 'string') {
    value = value.trim();

    // 通过遍历 memoryUnits 统一匹配所有单位，包括 m 单位
    for (const unit of Object.keys(memoryUnits) as MemoryUnit[]) {
      if (value.endsWith(unit)) {
        const num = parseFloat(value.slice(0, -unit.length));
        return { bytes: num * memoryUnits[unit], unit };
      }
    }

    if (/^\d*\.?\d+$/.test(value)) {
      return { bytes: parseFloat(value), unit: 'B' };
    }
  }

  return { bytes: 0, unit: 'B' };
}

function findAppropriateUnit(bytes: number, options: CalculateMemoryOptions = {}): MemoryUnit {
  // 如果强制指定单位，直接返回
  if (options.forceUnit) return options.forceUnit;

  const minUnit = options.minUnit || 'B';
  const minUnitIndex = unitPriority.indexOf(minUnit);

  // 从大到小查找合适的单位
  for (let i = unitPriority.length - 1; i >= 0; i--) {
    const unit = unitPriority[i];
    if (unitPriority.indexOf(unit) >= minUnitIndex && bytes >= memoryUnits[unit]) {
      return unit;
    }
  }

  return minUnit;
}
